export const DEFAULT_API_BASE = 'https://api.axionquant.com';
export const LOGO_BASE_URL = 'https://s3-symbol-logo.tradingview.com/';

/**
 * Fetches the raw SVG markup for a logo so it can be inlined into
 * the page instead of loaded via an `<img src>` request at runtime. Strips
 * surrounding HTML comments, and rejects anything that isn't a plain `<svg>`
 * (no scripts or event-handler attributes). The result is passed through
 * `normalizeLogoSvgSize()` so it always scales to fit its container.
 */
async function fetchLogoSvg(logoUrl: string) {
  try {
    const res = await fetch(logoUrl);
    if (!res.ok) return null;
    const svg = (await res.text()).replace(/<!--[\s\S]*?-->/g, '').trim();
    if (!/^<svg[\s>]/i.test(svg)) return null;
    if (/<script|onload\s*=|onerror\s*=|javascript:/i.test(svg)) return null;
    return normalizeLogoSvgSize(svg);
  } catch {
    return null;
  }
}

/**
 * Makes an inlined logo SVG scale to fill its container. Many vendor SVGs
 * ship with hard-coded pixel dimensions and no `viewBox` (e.g. 
 * `<svg width="18" height="18">`), which pins the artwork to a tiny fixed
 * coordinate system regardless of how large the element is styled to be.
 * This synthesizes a `viewBox` from the intrinsic pixel size when missing,
 * then removes the fixed `width`/`height` attributes so container CSS
 * (e.g. `width: 100%; height: 100%`) fully controls the rendered size.
 * Aspect ratio is preserved via the SVG default `preserveAspectRatio`.
 */
function normalizeLogoSvgSize(svg: string): string {
  const rootTagMatch = svg.match(/^<svg\b[^>]*>/i);
  if (!rootTagMatch) return svg;
  const rootTag = rootTagMatch[0];

  const getAttr = (name: string): string | null => {
    const m = new RegExp(`\\s${name}=("[^"]*"|'[^']*')`, 'i').exec(rootTag);
    return m ? m[1].slice(1, -1) : null;
  };

  let updated = rootTag;
  if (!/\sviewBox=/i.test(updated)) {
    const w = getAttr('width');
    const h = getAttr('height');
    if (w && h && /^\d+(\.\d+)?$/.test(w.trim()) && /^\d+(\.\d+)?$/.test(h.trim())) {
      updated = updated.replace(/^<svg\b/i, `<svg viewBox="0 0 ${w.trim()} ${h.trim()}"`);
    }
  }
  updated = updated.replace(/\s(width|height)=("[^"]*"|'[^']*')/gi, '');

  return updated === rootTag ? svg : svg.replace(rootTag, updated);
}

async function axionGet(
  baseUrl: string,
  path: string,
  params?: Record<string, unknown>,
  apiKey?: string
) {
  try {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const query = qs.toString();
    const url = `${baseUrl}${path}${query ? `?${query}` : ''}`;
    const res = await fetch(url, {
      headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export interface TickerReportOptions {
  apiBaseUrl?: string;
  apiKey?: string;
}

/**
 * Fetches every dataset a ticker report page can render, in parallel, and
 * returns a single plain-object payload. Individual endpoint failures resolve
 * to `null` rather than rejecting, so one missing dataset never breaks the
 * page. Intended to run server-side at build time (never in the browser).
 */
export async function fetchTickerReportData(rawTicker: string, options: TickerReportOptions = {}) {
  const { apiBaseUrl = DEFAULT_API_BASE, apiKey } = options;
  const ticker = String(rawTicker || '').toUpperCase().trim();
  if (!ticker) return null;

  const get = (path: string, params?: Record<string, unknown>) => axionGet(apiBaseUrl, path, params, apiKey);

  const identity = await get(`/stocks/${ticker}`);
  if (!identity) return { ticker, notFound: true };

  const periods = 8;
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  const from = fiveYearsAgo.toISOString().slice(0, 10);

  const [
    logo,
    prices,
    profile,
    statistics,
    summary,
    recommendation,
    revenue,
    netincome,
    fcf,
    capex,
    eps,
    roe,
    de,
    snapshot,
    metrics,
    dcf,
    esg,
    ownership,
    transactions,
    funds,
    suppliers,
    customers,
    peers,
    socialSentiment,
    newsSentiment,
    analystSentiment,
    filings,
    news,
  ] = await Promise.all([
    get(`/stocks/${ticker}/logo`),
    get(`/stocks/${ticker}/prices`, { frame: 'weekly' }),
    get(`/profiles/${ticker}`),
    get(`/profiles/${ticker}/statistics`),
    get(`/profiles/${ticker}/summary`),
    get(`/profiles/${ticker}/recommendation`),
    get(`/financials/${ticker}/revenue`, { periods }),
    get(`/financials/${ticker}/netincome`, { periods }),
    get(`/financials/${ticker}/cashflow/free`, { periods }),
    get(`/financials/${ticker}/capitalexpenditures`, { periods }),
    get(`/financials/${ticker}/eps`, { from }),
    get(`/financials/${ticker}/roe`, { from }),
    get(`/financials/${ticker}/de`, { from }),
    get(`/financials/${ticker}/snapshot`),
    get(`/financials/${ticker}/metrics`),
    get(`/financials/dcf/${ticker}/value`),
    get(`/esg/${ticker}`),
    get(`/insiders/${ticker}/ownership`),
    get(`/insiders/${ticker}/transactions`),
    get(`/insiders/${ticker}/funds`), // ETF / mutual-fund holders of this stock
    get(`/supply-chain/${ticker}/suppliers`),
    get(`/supply-chain/${ticker}/customers`),
    get(`/supply-chain/${ticker}/peers`),
    get(`/sentiment/${ticker}/social`),
    get(`/sentiment/${ticker}/news`),
    get(`/sentiment/${ticker}/analyst`),
    get(`/filings/${ticker}`, { limit: 5 }),
    get(`/news/${ticker}`),
  ]);

  const logoUrl = logo?.logo ? `${LOGO_BASE_URL}${logo.logo}.svg` : null;
  const logoSvg = logo?.logo ? await fetchLogoSvg(`${LOGO_BASE_URL}${logo.logo}.svg`) : null;

  const sentiment =
    socialSentiment || newsSentiment || analystSentiment
      ? { socialSentiment, newsSentiment, analystSentiment }
      : null;

  return {
    ticker,
    identity,
    logoUrl,
    logoSvg,
    prices,
    profile,
    statistics,
    summary,
    recommendation,
    revenue,
    netincome,
    fcf,
    capex,
    eps,
    roe,
    de,
    snapshot,
    metrics,
    dcf,
    esg,
    ownership,
    transactions,
    funds,
    suppliers,
    customers,
    peers,
    sentiment,
    filings,
    news,
  };
}
